import React, {
  useCallback,
  useMemo,
  useState,
  useContext,
  useEffect,
} from 'react';
import { FormOutlined, SettingOutlined } from '@ant-design/icons';

import BorderlessButton from '../../common/buttons/BorderlessButton';

import WorkflowActionNode from './WorkflowActionNode';
import WorkflowHandle from './WorkflowHandle';
import WorkflowTriggerConfigureDrawer from './WorkflowTriggerConfigureDrawer';
import UpdateAddTimestampCheckbox from './UpdateAddTimestampCheckbox';

import {
  locateWorkflowParentElement,
  updateData,
} from './workflowHelpers';
import { createSourceToTargetMap, resolveFormDivisionIds } from '../formHelpers';
import FormWorkflowContext from './FormWorkflowContext';
import useGetFormDropdownField from '../../common/hooks/useGetFormDropdownField';
import { isFieldValid, validFieldTypes } from './update.constants';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';

const DISABLED_TEXT = 'Add a Form Submissions Dropdown field to enable this step';
const HOVER_TEXT = `
This step requires one Form Submissions Dropdown field. Use this step to update another form.
`;

const ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires at least one field mapping set via <b> Configure </b> <br />
    </div>
  ),
};

export default function WorkflowFormUpdateNode({
  setDataMap,
  setElements,
  isDisplay,
  divisionId,
  divisionIds = [],
  templateId,
  name,
  sections = [],
  setDisableNext,
} = {}) {
  const relevantDivisionIds = resolveFormDivisionIds({ divisionIds, divisionId });

  return function WorkflowFormUpdateNodeComponent({
    id,
    disabled,
    draggable,
    data = {},
  }) {
    const {
      elements = [],
      dataMap,
    } = useContext(FormWorkflowContext) ?? {};

    const parentLogicalLoopNode = useMemo(() => {
      const logicalLoopNode = locateWorkflowParentElement(id, 'logicalLoop', elements);

      if (!logicalLoopNode) return logicalLoopNode;

      return {
        ...logicalLoopNode,
        data: dataMap[logicalLoopNode.id] ?? logicalLoopNode.data,
      };
    }, [id, elements, dataMap]);

    const {
      fieldMappings: initialFieldMappings = {},
      shouldOverrideForm: initialShouldOverrideForm = true,
      addTimestamps,
    } = data;

    const { relevantCompletedField: relevantField } = useGetFormDropdownField({ sections });

    const [showDrawer, setShowDrawer] = useState(false);
    const [fieldMappings, setFieldMappings] = useState(initialFieldMappings);
    const [override, setOverride] = useState(initialShouldOverrideForm);
    const [selectedAddTimestamps, setSelectedAddTimestamps] = useState(addTimestamps);
    const [selectedTimestampConfig, setSelectedTimestampConfig] = useState(
      data.timestampConfig || {
        source: 'stepExecution',
        includeTime: false,
      },
    );

    const openDrawer = useCallback(() => setShowDrawer(true), []);
    const closeDrawer = useCallback(() => setShowDrawer(false), []);

    useEffect(() => {
      setSelectedAddTimestamps(addTimestamps);
    }, [addTimestamps]);

    const hasValidMappings = useMemo(() => {
      if (!fieldMappings) return false;

      return Object.keys(fieldMappings).some((key) => {
        if (key === 'version') return false;
        const mappings = fieldMappings[key] ?? {};
        return Object.values(mappings)?.length > 0;
      });
    }, [fieldMappings]);

    useEffect(() => {
      if (!setDisableNext) return;
      setDisableNext((prev) => ({ ...prev, [id]: !hasValidMappings }));
    }, [setDisableNext, id, hasValidMappings]);

    const onFieldMappingsChange = useCallback((newMappings, formId) => {
      if (!setDataMap || !id) return;
      const updatedFieldMappings = {
        ...fieldMappings,
        version: 'v2',
        [formId]: createSourceToTargetMap(newMappings),
      };

      setFieldMappings(updatedFieldMappings);
      setDataMap(
        updateData(id, { fieldMappings: updatedFieldMappings }),
      );
    }, [setDataMap, id, fieldMappings, setDisableNext]);

    const onCheckChanged = useCallback((checked) => {
      setDataMap(updateData(id, { shouldOverrideForm: checked }));
      setOverride(checked);
    }, [setDataMap, id]);

    const onTimestampChanged = useCallback((newValue) => {
      setSelectedAddTimestamps(newValue);
      setDataMap(updateData(id, { addTimestamps: newValue }));
    }, [setDataMap, id]);

    const onTimestampConfigChanged = useCallback((newConfig) => {
      setSelectedTimestampConfig(newConfig);
      setDataMap(updateData(id, { timestampConfig: newConfig }));
    }, [setDataMap, id]);

    const title = `Form Update${disabled ? ' - DISABLED' : ''}`;

    return (
      <WorkflowActionNode
        title={title}
        Icon={FormOutlined}
        type="formUpdate"
        id={id}
        draggable={draggable}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        isDisplay={isDisplay || disabled}
        hover={disabled ? DISABLED_TEXT : HOVER_TEXT}
        style={disabled ? { opacity: 0.7 } : { }}
        errorPopover={!hasValidMappings ? ERROR_DETAILS : null}
      >
        {!draggable && (
          <div>

            <OnTraccrCheckbox
              style={{ marginTop: 10 }}
              onChange={onCheckChanged}
              defaultChecked={initialShouldOverrideForm}
              value={override}
              label="Override Form"
              hoverNode={(
                <div style={{ width: 300 }}>
                  If turned on, the form data will be overwritten with the new data.
                  <br />
                  <br />
                  If turned off, the form data will be appended to the existing data.
                  <br />
                  <br />
                  Note: Date/Time and Date/Time Range fields will always override existing data.
                </div>
              )}
            />
            {!override && (
              <UpdateAddTimestampCheckbox
                onChange={onTimestampChanged}
                initialChecked={addTimestamps}
                value={selectedAddTimestamps}
                sections={sections}
                onTimestampConfigChange={onTimestampConfigChanged}
                timestampConfig={selectedTimestampConfig}
              />
            )}
            <BorderlessButton
              title="Configure"
              style={{ margin: 5 }}
              iconNode={<SettingOutlined />}
              onClick={openDrawer}
            />
            <WorkflowTriggerConfigureDrawer
              visible={showDrawer}
              onClose={closeDrawer}
              onSubmit={closeDrawer}
              targetFormId={relevantField?.configProps?.subDataType}
              sourceName={name}
              sourceSections={sections}
              fieldMappings={fieldMappings}
              onFieldMappingsChange={onFieldMappingsChange}
              divisionId={divisionId}
              divisionIds={relevantDivisionIds}
              templateId={templateId}
              parentLogicalLoopNode={parentLogicalLoopNode}
              validFieldTypes={validFieldTypes}
              isFieldValid={isFieldValid}
            />
            <WorkflowHandle type="target" position="top" disabled={isDisplay} />
          </div>
        )}
      </WorkflowActionNode>
    );
  };
}
